import { PLACEHOLDER } from '../data/homeData';
import { buildProductImageSource } from './productImage';
import { parseProductPrice } from './price';

export function sortApiProducts(items) {
  return items.slice().sort((a, b) => {
    const categorySortDiff = Number(a.category?.sort ?? 0) - Number(b.category?.sort ?? 0);
    if (categorySortDiff !== 0) return categorySortDiff;

    const sortDiff = Number(a.sort) - Number(b.sort);
    if (sortDiff !== 0) return sortDiff;

    return Number(a.id) - Number(b.id);
  });
}

function formatMoney(value) {
  const num = parseProductPrice(value);
  if (num <= 0) return null;
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

function resolveProductPricing(apiProduct) {
  const listPrice = parseProductPrice(apiProduct.price);
  const discountField = parseProductPrice(
    apiProduct.discount ?? apiProduct.discount_amount ?? apiProduct.discount_price,
  );
  const explicitOriginal = parseProductPrice(
    apiProduct.original_price ?? apiProduct.old_price ?? apiProduct.compare_at_price,
  );

  if (explicitOriginal > 0 && listPrice > 0 && listPrice < explicitOriginal) {
    return {
      price: formatMoney(listPrice),
      originalPrice: formatMoney(explicitOriginal),
      discountPercent: Math.round(((explicitOriginal - listPrice) / explicitOriginal) * 100) || null,
    };
  }

  if (listPrice > 0 && discountField > 0) {
    let salePrice = listPrice;
    let discountPercent = null;

    if (discountField <= 100) {
      discountPercent = Math.round(discountField);
      salePrice = listPrice - (listPrice * discountField) / 100;
    } else if (discountField < listPrice) {
      salePrice = discountField;
      discountPercent = Math.round(((listPrice - salePrice) / listPrice) * 100);
    } else {
      salePrice = Math.max(0, listPrice - discountField);
      discountPercent = Math.round(((listPrice - salePrice) / listPrice) * 100);
    }

    if (salePrice > 0 && salePrice < listPrice) {
      return {
        price: formatMoney(salePrice),
        originalPrice: formatMoney(listPrice),
        discountPercent: discountPercent > 0 ? discountPercent : null,
      };
    }
  }

  if (listPrice > 0) {
    return {
      price: formatMoney(listPrice),
      originalPrice: null,
      discountPercent: null,
    };
  }

  if (discountField > 0) {
    return {
      price: formatMoney(discountField),
      originalPrice: null,
      discountPercent: null,
    };
  }

  return {
    price: null,
    originalPrice: null,
    discountPercent: null,
  };
}

export function isProductActive(product) {
  if (product == null) return true;
  if (typeof product.isActive === 'boolean') return product.isActive;
  const raw = product.is_active ?? product.isActive;
  if (raw == null) return true;
  return Number(raw) === 1;
}

export function isProductVip(product) {
  if (product == null) return false;
  if (typeof product.isVip === 'boolean') return product.isVip;
  const raw = product.is_vip ?? product.isVip;
  if (raw == null) return false;
  return raw === true || raw === 'true' || Number(raw) === 1;
}

export function isProductHidePrice(product) {
  if (product == null) return false;
  if (typeof product.hidePrice === 'boolean') return product.hidePrice;
  const raw = product.hide_price ?? product.hidePrice;
  if (raw == null) return false;
  return raw === true || raw === 'true' || Number(raw) === 1;
}

function collectProductImages(apiProduct) {
  const urls = [];
  const push = (url) => {
    const src = buildProductImageSource(url);
    if (src && !urls.includes(src)) urls.push(src);
  };

  if (Array.isArray(apiProduct.images)) {
    for (const item of apiProduct.images) {
      if (typeof item === 'string') push(item);
      else push(item?.url ?? item?.image_url);
    }
  }

  push(apiProduct.image_url);
  return urls;
}

export function mapApiProduct(apiProduct) {
  const pricing = resolveProductPricing(apiProduct);
  const images = collectProductImages(apiProduct);

  return {
    id: String(apiProduct.id),
    name: apiProduct.name,
    description: apiProduct.description ?? '',
    image: images[0] ?? PLACEHOLDER,
    images: images.length ? images : [PLACEHOLDER],
    videoUrl: apiProduct.video_url || apiProduct.videoUrl || null,
    discountPercent: pricing.discountPercent,
    price: pricing.price,
    originalPrice: pricing.originalPrice,
    isActive: isProductActive(apiProduct),
    isVip: isProductVip(apiProduct),
    hidePrice: isProductHidePrice(apiProduct),
  };
}
